import React, { useEffect, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import axios from "axios";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { Percent } from "lucide-react";
import { getContractorId } from "Common/Components/getRoleId";

const API_BASE_URL = "https://api.havirkesht.ir";
const COMMISSION_URL = `${API_BASE_URL}/contractor-settings/commission-percentage`;

const CommissionPercentage = () => {
  document.title = "تنظیم درصد پورسانت | سامانه هاویر کشت";

  const token = localStorage.getItem("app_atkey");
  const tokenContractorId = getContractorId(token);

  // state‌ها
  const [contractorId, setContractorId] = useState<any>(
    tokenContractorId ?? 0
  );
  const [contractorList, setContractorList] = useState<any[]>([]);
  const [current, setCurrent] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  // ---- دریافت نقش کاربر از توکن JWT ----
  useEffect(() => {
    const getUserRoleFromToken = () => {
      if (!token) return null;
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload?.role || null; // فرض می‌کنیم role در payload با کلید "role" ذخیره شده
      } catch {
        return null;
      }
    };
    const role = getUserRoleFromToken();
    setIsSuperAdmin(role === "superadmin");
  }, [token]);

  // ---- لیست پیمانکاران (فقط برای سوپرادمین) ----
  useEffect(() => {
    const fetchContractors = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/public/contractors`);
        const list = res?.data?.contractors ?? [];
        setContractorList(Array.isArray(list) ? list : []);
      } catch (error) {
        setContractorList([]);
      }
    };
    if (isSuperAdmin) {
      fetchContractors();
    } else {
      // اگر سوپرادمین نیست، لیست را خالی می‌کنیم
      setContractorList([]);
    }
  }, [isSuperAdmin]);

  // ---- دریافت درصد پورسانت ----
  useEffect(() => {
    const fetchCommission = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        let url = COMMISSION_URL;
        // اگر سوپرادمین باشد و contractorId مشخص شده باشد، آن را به query اضافه می‌کنیم
        if (isSuperAdmin && contractorId) {
          url += `?contractor_id=${contractorId}`;
        }
        const res = await axios.get(url, { headers: authHeaders });
        setCurrent(res?.data);
      } catch (error: any) {
        setCurrent(null);
        if (error?.response?.status === 403) {
          setErrorMessage("دسترسی به تنظیمات این پیمانکار را ندارید.");
        } else if (error?.response?.status === 404) {
          setErrorMessage("برای این پیمانکار تنظیماتی ثبت نشده است.");
        } else {
          setErrorMessage("خطا در دریافت درصد پورسانت. مجدداً تلاش کنید.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommission();
  }, [contractorId, isSuperAdmin]);

  // ---- فرم ----
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      commission_percentage:
        current?.commission_percentage !== undefined &&
          current?.commission_percentage !== null
          ? String(current.commission_percentage)
          : "",
    },
    validationSchema: Yup.object({
      commission_percentage: Yup.number()
        .typeError("درصد پورسانت باید یک عدد باشد")
        .min(0, "درصد پورسانت باید بین ۰ تا ۱۰۰ باشد")
        .max(100, "درصد پورسانت باید بین ۰ تا ۱۰۰ باشد")
        .required("درصد پورسانت نباید خالی باشد"),
    }),
    onSubmit: async (values) => {
      const newValue = Number(values.commission_percentage);
      const contractorName =
        current?.contractor_name ||
        (isSuperAdmin ? contractorId : tokenContractorId) ||
        "پیمانکار";

      const result = await Swal.fire({
        title: "تأیید تغییر درصد پورسانت",
        text: `درصد پورسانت پیمانکار «${contractorName}» به ${newValue.toLocaleString(
          "fa-IR"
        )} درصد تغییر کند؟`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "بله",
        cancelButtonText: "خیر",
        reverseButtons: true,
      });
      if (!result.isConfirmed) return;

      setIsSaving(true);
      try {
        let url = COMMISSION_URL;
        const payload: any = { commission_percentage: newValue };
        if (isSuperAdmin && contractorId) {
          url += `?contractor_id=${contractorId}`;
        }
        const res = await axios.put(url, payload, {
          headers: { ...authHeaders, "Content-Type": "application/json" },
        });
        setCurrent(res?.data);
        Swal.fire({
          title: "موفقیت آمیز",
          text: "درصد پورسانت با موفقیت ذخیره شد.",
          icon: "success",
          confirmButtonText: "متوجه شدم",
        });
      } catch (error: any) {
        if (error?.response?.status === 403) {
          toast.error("اجازه تغییر تنظیمات این پیمانکار را ندارید");
        } else if (error?.response?.status === 422) {
          toast.error("مقدار وارد شده معتبر نیست (باید بین ۰ تا ۱۰۰ باشد)");
        } else {
          toast.error("مشکلی در ذخیره درصد پورسانت رخ داد");
        }
      } finally {
        setIsSaving(false);
      }
    },
  });

  return (
    <React.Fragment>
      <BreadCrumb pageTitle="تنظیم درصد پورسانت" title="داده های اولیه" />
      <ToastContainer closeButton={false} limit={1} />
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
            <div className="xl:col-span-7">
              <h6 className="mb-4 text-15 dark:text-white">
                درصد پورسانت (کارمزد) پیمانکار
              </h6>

              {/* انتخاب پیمانکار (فقط برای سوپرادمین) */}
              {isSuperAdmin ? (
                <div className="mb-4">
                  <label
                    htmlFor="contractor_id"
                    className="inline-block mb-2 text-base font-medium dark:text-white"
                  >
                    پیمانکار:
                  </label>
                  <select
                    id="contractor_id"
                    name="contractor_id"
                    className="form-input w-full p-2 border border-gray-200 rounded bg-white text-gray-900 dark:text-white dark:bg-[#132337]"
                    value={contractorId}
                    onChange={(e) => setContractorId(Number(e.target.value))}
                  >
                    {contractorList.map((item: any) => (
                      <option key={item.id} value={item.id}>
                        {`شناسه پیمانکار ${item.id}`}
                        {item.id === tokenContractorId
                          ? " (پیمانکار شما)"
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mb-4 p-2 bg-slate-50 dark:bg-zink-700 rounded">
                  <span className="text-sm">
                    پیمانکار شما:{" "}
                    <b>
                      {current?.contractor_name ||
                        `شناسه ${tokenContractorId}` ||
                        "نامشخص"}
                    </b>
                  </span>
                </div>
              )}

              {/* نمایش اطلاعات فعلی */}
              <div className="p-4 mb-4 rounded-md bg-slate-100 dark:bg-zink-600 dark:text-white">
                {isLoading ? (
                  <span>در حال دریافت اطلاعات...</span>
                ) : errorMessage ? (
                  <span className="text-red-500">{errorMessage}</span>
                ) : (
                  <div className="flex flex-col gap-1">
                    <span>
                      نام پیمانکار: <b>{current?.contractor_name || "-"}</b>
                    </span>
 

                    <span>
                      درصد پورسانت فعلی:{" "}
                      <b>
                        {Number(
                          current?.commission_percentage ?? 0
                        ).toLocaleString("fa-IR")}
                        ٪
                      </b>
                    </span>
                  </div>
                )}
              </div>

              {/* فرم ویرایش */}
              <form onSubmit={validation.handleSubmit}>
                <label
                  htmlFor="commission_percentage"
                  className="inline-block mb-2 text-base font-medium dark:text-white"
                >
                  درصد پورسانت جدید (۰ تا ۱۰۰):
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    id="commission_percentage"
                    name="commission_percentage"
                    autoComplete="off"
                    disabled={isLoading || !!errorMessage}
                    className="form-input w-full p-2 pl-8 border border-gray-200 rounded bg-white text-gray-900 dark:text-white dark:bg-[#132337]"
                    placeholder="مثلاً 1 یا 0"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.commission_percentage}
                  />
                  <span className="absolute inset-y-0 left-2 flex items-center text-gray-400">
                    <Percent size={15} />
                  </span>
                </div>
                {validation.touched.commission_percentage &&
                  validation.errors.commission_percentage && (
                    <p className="mt-2 text-red-400">
                      {validation.errors.commission_percentage}
                    </p>
                  )}

                <button
                  type="submit"
                  disabled={isSaving || isLoading || !!errorMessage}
                  className="px-4 py-2 mt-4 font-bold text-white bg-blue-500 rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {isSaving ? "در حال ذخیره ..." : "ذخیره"}
                </button>
              </form>

              <p className="mt-4 text-sm text-slate-500 dark:text-zink-200">
                این درصد در محاسبه «کارمزد» صورتحساب کشاورزان همین پیمانکار
                استفاده می‌شود. مقدار ۰ یعنی کارمزدی کسر نمی‌شود.
              </p>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CommissionPercentage;